"use client"

import { css } from "@emotion/css"
import styled from "@emotion/styled"
import { LanguageTranslation, XmarkCircle } from "@vectopus/atlas-icons-react"
import React, { useContext } from "react"
import { useTranslation } from "react-i18next"

import PseudoContentLink from "@/components/PseudoContentLink"
import SettingIcon from "@/imgs/setting.svg"
import LoginStateContext from "@/shared-module/common/contexts/LoginStateContext"
import { baseTheme, headingFont, primaryFont } from "@/shared-module/common/styles"
import { respondToOrLarger } from "@/shared-module/common/styles/respond"
import ietfLanguageTagToHumanReadableName from "@/shared-module/common/utils/ietfLanguageTagToHumanReadableName"

const CourseGrid = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  padding-bottom: 10px;

  ${respondToOrLarger.md} {
    grid-template-columns: 1fr 1fr;
  }

  ${respondToOrLarger.xxl} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const StyledCourseCard = styled.div`
  margin-bottom: 5px;

  position: relative;
  max-width: 100%;
  width: 100%;
  min-height: 220px;
  background: #f5f6f7;
  border-radius: 6px;

  border: 2px solid #e5e7e9;
  :focus-visible {
    outline: 2px solid ${baseTheme.colors.green[500]};
    outline-offset: 2px;
  }

  :hover {
    cursor: pointer;
    background: #ebedee;
  }
`

const CourseWrapper = styled.div`
  width: 100%;
  background: #f5f6f7;
  border-radius: 6px;
  :hover {
    cursor: pointer;
    background: #ebedee;
  }
`

const CourseContent = styled.div`
  padding: 2rem 1.5rem 4.5rem;
`

const CourseHeading = styled.div`
  font-family: ${headingFont};
  font-weight: 550;
  font-size: 24px;
  line-height: 120%;
  color: #1a2333;
  margin-bottom: 13px;
`

const CourseDescription = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;

  /* Limit line count to 3 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  /* stylelint-disable-next-line property-no-deprecated */
  -webkit-box-orient: vertical;

  font-family: ${primaryFont};
  font-weight: 400;
  font-size: 18px;
  line-height: 24px;
  color: #1a2333;
  opacity: 0.75;
`

const CourseLanguageContent = styled.div`
  margin-top: 25px;
  display: flex;
  padding: 0px 28px 0rem 1.5rem;
  align-items: center;
  gap: 8px;

  position: absolute;
  bottom: 20px;
`

const LanguageCode = styled.div`
  font-family: ${primaryFont};
  font-weight: 450;
  font-size: 18px;
  color: #1a2333;
`

interface CourseCardProps {
  title: string
  isDraft: boolean
  isUnlisted: boolean
  description: string
  languageCode: string
  manageHref: string
  navigateToCourseHref: string
  id: string
  showManageButton: boolean
  /** When provided, a control to hide the course from the list is shown. */
  onHide?: () => void
}

const capitalizeFirstLetter: (language: string) => string = (language) => {
  if (!language) {
    return language
  }
  return language.charAt(0).toUpperCase() + language.slice(1).toLowerCase()
}

const CourseCard: React.FC<React.PropsWithChildren<CourseCardProps>> = ({
  title,
  isDraft,
  isUnlisted,
  description,
  languageCode,
  manageHref,
  navigateToCourseHref,
  showManageButton,
  onHide,
}) => {
  const loginStateContext = useContext(LoginStateContext)
  const { t } = useTranslation()
  const languageName = ietfLanguageTagToHumanReadableName(languageCode)

  return (
    <StyledCourseCard>
      {onHide && (
        <button
          type="button"
          className={css`
            position: absolute;
            top: 12px;
            right: 12px;
            z-index: 110;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            border: none;
            background: transparent;
            color: #1a2333;
            opacity: 0.6;
            border-radius: 4px;

            :hover {
              cursor: pointer;
              opacity: 1;
            }

            :focus-visible {
              outline: 2px solid ${baseTheme.colors.green[500]};
              outline-offset: 2px;
            }
          `}
          aria-label={t("hide-course", { title })}
          onClick={onHide}
        >
          <XmarkCircle size={20} aria-hidden="true" />
        </button>
      )}
      {loginStateContext.signedIn && showManageButton && (
        <a
          className={css`
            :focus-visible > * {
              outline: 2px solid ${baseTheme.colors.green[500]};
              outline-offset: 2px;
            }
            outline: none;
            position: absolute;
            bottom: 24px;
            right: 16px;
            opacity: 0.6;
            z-index: 110;

            :hover {
              cursor: pointer;
              opacity: 1;
            }
          `}
          aria-label={t("manage-course", { title })}
          href={manageHref}
        >
          <SettingIcon />
        </a>
      )}

      <CourseWrapper>
        <CourseContent>
          <PseudoContentLink href={navigateToCourseHref}>
            <CourseHeading>
              {title}
              {isDraft && ` (${t("draft")})`}
              {isUnlisted && ` (${t("unlisted")})`}
            </CourseHeading>
          </PseudoContentLink>
          <CourseDescription>{description}</CourseDescription>
        </CourseContent>
        <CourseLanguageContent>
          {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- span+role=img wraps an SVG icon; <img> tag unusable */}
          <span role="img" aria-label={t("language-icon")}>
            <LanguageTranslation
              className={css`
                position: relative;
                top: 2px;
              `}
              size={20}
              aria-hidden="true"
            />
          </span>
          <LanguageCode>{capitalizeFirstLetter(languageName)}</LanguageCode>
        </CourseLanguageContent>
      </CourseWrapper>
    </StyledCourseCard>
  )
}

export { CourseGrid }
export default CourseCard
